// @flow
import { List } from "immutable";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";
import { createListSelector } from "reselect-map";

import {
  OUTLINE_CELL_CHILD_ARROW_PADDING_LEFT,
  OUTLINE_CELL_CHILD_ARROW_WIDTH,
  OUTLINE_CELL_LABEL_ICON_SIZE,
  TABLE_CARD_INDENT_PADDING,
  TABLE_CARD_INDENT_PADDING_WITH_CHILDREN,
  TABLE_CELL_ICON_MARGIN_LEFT,
  TABLE_CELL_TEXT_AREA_PADDING_HORIZONTAL
} from "../../constants";
import {
  GLOBAL_FILTER_ID,
  ITEM_CARD_NARRATIVE_LIST_VIEW,
  ITEM_CARD_SEQUENCE_LIST_VIEW,
  MINDMAP_VIEW,
  NARRATIVE_VIEW,
  OUTLINE_VIEW,
  RELATIONSHIP_VIEW,
  SPREADSHEET_VIEW,
  SUBWAY_VIEW,
  TIMELINE_VIEW
} from "../../constants/identifiers";
import { METRICS_PREFIX } from "../../constants/itemFields";
import outlineColumnIds from "../../constants/outlineColumnIds";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { appStateSelectors } from "../../modules/appState";
import { characterWidthMapSelectors } from "../../modules/characterWidthMap";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import { displayCacheSelectors } from "../../modules/display";
import { filterSelectors } from "../../modules/filters";
import { itemSelectors } from "../../modules/items";
import { narrativeSelectors } from "../../modules/narrative";
import { relationshipSelectors } from "../../modules/relationships";
import superAndChildrenSelectors from "../../modules/superAndChildrenById/selectors";
import { uiStateSelectors } from "../../modules/uiState";
import { calcTextSize } from "../../utils/calcTextSize";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import windowDimensions from "../../utils/windowDimensions";
import itemCardFunctions from "../itemCard/itemCardFunctions";
import { displayItemSelectors } from "../sharedSelectors/displayItems";

const getViewId = (state, viewId) => viewId;

/**
 * @param {TimelineRecord} state
 */
const getItemIdsMatchingGlobalFilter = createSelector(
  // fetch parameters for last calculate function
  displayCacheSelectors.getDisplayItemsById,
  superAndChildrenSelectors.getSuperAndChildrenById,
  state => state.data.items.allIds,
  appStateSelectors.getGlobalFilter,
  state => state.data.narrative,
  state => state.data.relationships.byId,
  calendarSelectors.getCalendar,
  // calculate result
  (displayItemsById, superAndChildrenById, itemIds, globalFilter, narrative, relationshipsById, calendar) => {
    return filterSelectors.itemIdsMatchingFilter(
      globalFilter,
      itemIds,
      displayItemsById,
      superAndChildrenById,
      narrative,
      relationshipsById,
      calendar,
      GLOBAL_FILTER_ID
    );
  }
);

/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getItemIdsMatchingFilterForView = createCachedSelector(
  // fetch parameters for last calculate function
  getItemIdsMatchingGlobalFilter,
  displayCacheSelectors.getDisplayItemsById,
  superAndChildrenSelectors.getSuperAndChildrenById,
  appStateSelectors.getViewFilter,
  (state, _viewId) => state.data.narrative,
  (state, _viewId) => state.data.relationships.byId,
  calendarSelectors.getCalendar,
  getViewId,
  // calculate result
  (itemIds, displayItemsById, superAndChildrenById, localFilter, narrative, relationshipsById, calendar, viewId) => {
    return filterSelectors.itemIdsMatchingFilter(
      localFilter,
      itemIds,
      displayItemsById,
      superAndChildrenById,
      narrative,
      relationshipsById,
      calendar,
      viewId
    );
  }
)(getViewId);

const getSelectedItemIdsMatchingFilterForView = createCachedSelector(
  getItemIdsMatchingFilterForView,
  uiStateSelectors.getSelectedItems,
  (idsForView, selectedIds) => selectedIds.filter(si => idsForView.contains(si))
)(getViewId);

// May choose to switch this to list selector if it is slow enough, but suspect it is a fast function so avoiding
// premature optimisation.
/**
 * @param {TimelineRecord} state
 */
const getTimelineViewDisplayItems = createSelector(
  // inputs
  displayCacheSelectors.getDisplayItemsById,
  displayCacheSelectors.getViewCardSizes,
  state => definitionSelectors.getTimelineViewSettings(state).showTypes,
  // calculate based on inputs
  (itemMap, viewCardSizes, showTypes) => {
    return itemMap
      .filter(obj => obj.startDate && obj.startDate.timestamp !== null && showTypes.includes(obj.type))
      .map(obj => obj.set("itemCardSize", viewCardSizes.get(obj.id).timelineCardSize));
  }
);

const getTimelineViewDisplayItemForId = (state, itemId) => {
  const items = displayItemSelectors.getDisplayItemsWithTextSizeList(state);
  const viewCardSizes = displayCacheSelectors.getViewCardSizes(state);
  const obj = items.find(o => o.id === itemId);
  if (obj) {
    return obj.set("itemCardSize", viewCardSizes.get(obj.id).timelineCardSize);
  }
  return null;
};

/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getNarrativeViewDisplayItems = createCachedSelector(
  // inputs
  state => displayItemSelectors.getDisplayItemsWithTextSizeList(state),
  displayCacheSelectors.getViewCardSizes,
  getItemIdsMatchingFilterForView,
  // calculate based on inputs
  (items, viewCardSizes, filteredItemIds) => {
    return items.map(obj => {
      const excludedByFilter = filteredItemIds ? !filteredItemIds.contains(obj.id) : false;
      const itemCardSize = viewCardSizes.get(obj.id).narrativeCardSize;
      return obj.merge({
        itemCardSize,
        excludedByFilter
      });
    });
  }
)(getViewId);

/**
 * @param {TimelineRecord} state
 */
const getRelationshipViewDisplayItems = createSelector(
  // inputs
  displayItemSelectors.getDisplayItemsWithTextSizeList,
  displayCacheSelectors.getViewCardSizes,
  // calculate based on inputs
  (items, viewCardSizes) => {
    return items.map(obj => obj.set("itemCardSize", viewCardSizes.get(obj.id).relationshipCardSize));
  }
);

const getMobileItemCardDisplayItems = (
  items,
  settings,
  fontSize,
  widthMap,
  metricsSuffix,
  viewWidth,
  showDragHandles
) => {
  return items.map(obj =>
    obj.set(
      "itemCardSize",
      itemCardFunctions.calcMobileItemCard(obj, settings, fontSize, widthMap, metricsSuffix, viewWidth, showDragHandles)
    )
  );
};

/**
 * @param {TimelineRecord} state
 * @param {number} maxWidth
 * @param {string} metricsSuffix
 */
const getItemCardListViewDisplayItems = createSelector(
  // inputs
  (state, maxWidth, _metricsSuffix, showDragHandles) =>
    displayItemSelectors.getDisplayItemsWithTextSizeList(state, maxWidth, true, showDragHandles),
  definitionSelectors.getMobileItemCardViewSettings,
  appStateSelectors.getFontSize,
  characterWidthMapSelectors.getCharacterWidthMap,
  (_state, _maxWidth, metricsSuffix) => metricsSuffix,
  (state, maxWidth) => maxWidth,
  (_state, _maxWidth, _metricsSuffix, showDragHandles) => showDragHandles,
  getMobileItemCardDisplayItems
);

// TODO: Need to consider efficiency of this.
// Unfortunately, every change to an item is going to trigger a re-calculation
// which is not ideal.
// Uses reselect-map so that we tokenise on each individual item
const _updateItemsOutlineViewSizeListSelector = createListSelector(
  // Get inputs to function
  // First input - used as key in cache
  items => items,
  // Other inputs - need to remain consistent to avoid calling selector
  (_items, labelColumn) => labelColumn,
  (_items, _labelColumn, summaryColumn) => summaryColumn,
  (_items, _labelColumn, _summaryColumn, fontSize) => fontSize,
  (_items, _labelColumn, _summaryColumn, _fontSize, widthMap) => widthMap,
  // Calculate result
  (obj, labelColumn, summaryColumn, fontSize, widthMap) => {
    let narrativeNestedTitleMetrics = obj.narrativeNestedTitleMetrics;
    let summaryMetrics = obj.summaryMetrics;

    let label = getLabelWithUntitled(obj.label);

    // Now calculate all of the relevant text metrics
    const SCALED_OUTLINE_CELL_CHILD_ARROW_WIDTH = scalePixelsToFontSize(OUTLINE_CELL_CHILD_ARROW_WIDTH, fontSize);
    const SCALED_OUTLINE_CELL_LABEL_ICON_SIZE = scalePixelsToFontSize(OUTLINE_CELL_LABEL_ICON_SIZE, fontSize);
    const SCALED_OUTLINE_CELL_ICON_MARGIN_LEFT = scalePixelsToFontSize(TABLE_CELL_ICON_MARGIN_LEFT, fontSize);

    if (labelColumn) {
      // What room is there for the label?
      // I am being lazy here and just always allowing for the arrow in Outline View right now.
      // This means we will occasionally wrap when we don't need to (if it would end in the last 16 pixels),
      // but saves me from looking up the narrative tree here...
      const scaledColumnWidth = scalePixelsToFontSize(labelColumn.width, fontSize);
      const iconWidths =
        SCALED_OUTLINE_CELL_CHILD_ARROW_WIDTH +
        OUTLINE_CELL_CHILD_ARROW_PADDING_LEFT +
        SCALED_OUTLINE_CELL_LABEL_ICON_SIZE +
        SCALED_OUTLINE_CELL_ICON_MARGIN_LEFT;
      const labelWidth = scaledColumnWidth - iconWidths - 2 * TABLE_CELL_TEXT_AREA_PADDING_HORIZONTAL;
      const narrativeDepth = obj.narrativePosition ? obj.narrativePosition.size - 1 : 0;
      const depthMultiplier = narrativeDepth > 6 ? 6 : narrativeDepth;
      const narrativeNestedTextWidth =
        labelWidth -
        (depthMultiplier > 0 ? TABLE_CARD_INDENT_PADDING : 0) -
        (depthMultiplier > 1 ? (depthMultiplier - 1) * TABLE_CARD_INDENT_PADDING_WITH_CHILDREN : 0);
      narrativeNestedTitleMetrics = calcTextSize(label, fontSize, widthMap, narrativeNestedTextWidth);
    }

    if (summaryColumn) {
      const scaledSummaryWidth = scalePixelsToFontSize(summaryColumn.width, fontSize);
      const summaryWidth = scaledSummaryWidth - 2 * TABLE_CELL_TEXT_AREA_PADDING_HORIZONTAL;
      summaryMetrics = calcTextSize(obj.summary, fontSize, widthMap, summaryWidth);
    }

    const result = obj.merge({
      narrativeNestedTitleMetrics,
      summaryMetrics
    });
    return result;
  }
);

// TODO: Need to consider efficiency of this.
// Unfortunately, every change to an item is going to trigger a re-calculation
// which is not ideal.
// Uses reselect-map so that we tokenise on each individual item
const _updateItemsSpreadsheetViewSizeListSelector = createListSelector(
  // Get inputs to function
  // First input - used as key in cache
  items => items,
  // Other inputs - need to remain consistent to avoid calling selector
  (_items, labelColumn) => labelColumn,
  (_items, labelColumn, summaryColumn) => summaryColumn,
  (_items, labelColumn, summaryColumn, fontSize) => fontSize,
  (_items, labelColumn, summaryColumn, _fontSize, widthMap) => widthMap,
  // Calculate result
  (obj, labelColumn, summaryColumn, fontSize, widthMap) => {
    let nestedTitleMetrics = obj.nestedTitleMetrics;
    let summaryMetrics = obj.summaryMetrics;

    let label = getLabelWithUntitled(obj.label);

    // Now calculate all of the relevant text metrics
    const SCALED_OUTLINE_CELL_CHILD_ARROW_WIDTH = scalePixelsToFontSize(OUTLINE_CELL_CHILD_ARROW_WIDTH, fontSize);

    if (labelColumn) {
      // What room is there for the label?
      const scaledColumnWidth = scalePixelsToFontSize(labelColumn.width, fontSize);
      const iconWidths =
        obj.children.size > 0 ? SCALED_OUTLINE_CELL_CHILD_ARROW_WIDTH + OUTLINE_CELL_CHILD_ARROW_PADDING_LEFT : 0;
      const labelWidth = scaledColumnWidth - iconWidths - 2 * TABLE_CELL_TEXT_AREA_PADDING_HORIZONTAL;
      const depthMultiplier = obj.superDepth > 6 ? 6 : obj.superDepth;
      const nestedTextWidth =
        labelWidth -
        (depthMultiplier > 0 ? TABLE_CARD_INDENT_PADDING : 0) -
        (depthMultiplier > 1 ? (depthMultiplier - 1) * TABLE_CARD_INDENT_PADDING_WITH_CHILDREN : 0);
      nestedTitleMetrics = calcTextSize(label, fontSize, widthMap, nestedTextWidth);
    }
    if (summaryColumn) {
      const scaledSummaryWidth = scalePixelsToFontSize(summaryColumn.width, fontSize);
      const summaryWidth = scaledSummaryWidth - 2 * TABLE_CELL_TEXT_AREA_PADDING_HORIZONTAL;
      summaryMetrics = calcTextSize(obj.summary, fontSize, widthMap, summaryWidth);
    }
    const result = obj.merge({
      nestedTitleMetrics,
      summaryMetrics
    });
    return result;
  }
);

// Wrapper for reselect-map function above so we don't create a new list if we don't have to.
/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getOutlineViewDisplayItems = createSelector(
  // Get inputs to function
  // First input - used as key in cache
  state => displayItemSelectors.getDisplayItemsWithTextSizeList(state),
  (state, viewId) => appStateSelectors.getOutlineOptionsForView(state, viewId),
  appStateSelectors.getFontSize,
  characterWidthMapSelectors.getCharacterWidthMap,
  // Calculate result
  (items, outlineOptions, fontSize, widthMap) => {
    const labelColumn = outlineOptions.columns.find(c => c.id === outlineColumnIds.label);
    const summaryColumn = outlineOptions.columns.find(c => c.id === outlineColumnIds.summary);
    return _updateItemsOutlineViewSizeListSelector(items, labelColumn, summaryColumn, fontSize, widthMap);
  }
);

/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getSpreadsheetViewDisplayItems = createSelector(
  // Get inputs to function
  // First input - used as key in cache
  state => displayItemSelectors.getDisplayItemsWithTextSizeList(state),
  (state, viewId) => appStateSelectors.getSpreadsheetOptionsForView(state, viewId),
  appStateSelectors.getFontSize,
  characterWidthMapSelectors.getCharacterWidthMap,
  // Calculate result
  (items, spreadsheetOptions, fontSize, widthMap) => {
    const labelColumn = spreadsheetOptions.columns.find(c => c.id === outlineColumnIds.label);
    const summaryColumn = spreadsheetOptions.columns.find(c => c.id === outlineColumnIds.summary);
    return _updateItemsSpreadsheetViewSizeListSelector(items, labelColumn, summaryColumn, fontSize, widthMap);
  }
);

const getMindmapViewDisplayItems = createSelector(
  // inputs
  displayCacheSelectors.getDisplayItemsById,
  displayCacheSelectors.getViewCardSizes,
  // calculate based on inputs
  (items, viewCardSizes) => {
    // When calling this, we always override showIdentifier to false because we don't want it shown
    // as part of the item card, we want it elsewhere
    const result = items.map(obj => obj.set("itemCardSize", viewCardSizes.get(obj.id).mindmapCardSize));
    return result;
  }
);

/**
 * @param {TimelineRecord} state
 */
const getSubwayViewDisplayItems = createSelector(
  // inputs
  displayItemSelectors.getDisplayItemsWithTextSizeList,
  definitionSelectors.getMindmapViewSettings,
  appStateSelectors.getFontSize,
  characterWidthMapSelectors.getCharacterWidthMap,
  // calculate based on inputs
  (items, settings, fontSize, widthMap) => {
    // When calling this, we always override showIdentifier to false because we don't want it shown
    // as part of the item card, we want it elsewhere

    return items.map(obj =>
      obj.set(
        "itemCardSize",
        itemCardFunctions.calcItemCardSize(obj, settings.set("showIdentifier", false), fontSize, widthMap)
      )
    );
  }
);

const getDisplayItemsForViewType = (state, viewType, viewId) => {
  let result = null;
  switch (viewType) {
    case TIMELINE_VIEW:
      result = getTimelineViewDisplayItems(state);
      break;
    case NARRATIVE_VIEW:
      result = getNarrativeViewDisplayItems(state, viewId);
      break;
    case RELATIONSHIP_VIEW:
      result = getRelationshipViewDisplayItems(state);
      break;
    case MINDMAP_VIEW:
      result = getMindmapViewDisplayItems(state);
      break;
    case SPREADSHEET_VIEW:
      result = getSpreadsheetViewDisplayItems(state, viewId);
      break;
    case OUTLINE_VIEW:
      result = getOutlineViewDisplayItems(state, viewId);
      break;
    case SUBWAY_VIEW:
      result = getSubwayViewDisplayItems(state);
      break;
    default:
      result = List([]);
      break;
  }
  return result;
};

const getDisplayItemsForViewTypeForMobile = (state, viewType, maxWidth = windowDimensions().width, showDragHandles) => {
  switch (viewType) {
    case ITEM_CARD_SEQUENCE_LIST_VIEW:
      return getItemCardListViewDisplayItems(state, maxWidth, METRICS_PREFIX.SEQUENCE, showDragHandles);
    case ITEM_CARD_NARRATIVE_LIST_VIEW:
      return getItemCardListViewDisplayItems(state, maxWidth, METRICS_PREFIX.NARRATIVE, showDragHandles);
    default:
      return List([]);
  }
};

const getDisplayItemsForViewTypeOfView = (state, viewId) => {
  const result = getDisplayItemsForViewType(state, appStateSelectors.getViewTypeForView(state, viewId), viewId);
  return result;
};

const getDisplayItemsForViewTypeOfViewForMobile = (state, maxWidth, showDragHandles) =>
  getDisplayItemsForViewTypeForMobile(
    state,
    appStateSelectors.getMobilePortraitViewType(state),
    maxWidth,
    showDragHandles
  );

const getItemIdsForView = createCachedSelector(
  // fetch parameters for last calculate function
  getItemIdsMatchingFilterForView,
  // calculate result
  filteredIds => filteredIds.toSet()
)(getViewId);

/**
 * This function needs to find the correct items for all of its views
 * And it must be reselect safe, so no passing state => state
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getItemIdsForViewIds = createCachedSelector(
  // fetch parameters for last calculate function
  // (state, viewIds) => state,
  displayCacheSelectors.getDisplayItemsById,
  superAndChildrenSelectors.getSuperAndChildrenById,
  state => relationshipSelectors.getRelationshipsById(state),
  state => filterSelectors.getFiltersById(state),
  (state, _viewId) => state.data.narrative,
  state => calendarSelectors.getCalendar(state),
  (state, viewIds) => viewIds,
  // calculate result
  (displayItemsById, superAndChildrenById, relationshipsById, filtersById, narrative, calendar, viewIds) => {
    // Get all itemIds and then reduce the list those that pass the global filter
    let itemIds = displayItemsById.map(i => i.id).toList();
    const globalFilter = filterSelectors.getFilterWithIdFromFilters(filtersById, GLOBAL_FILTER_ID);
    itemIds = filterSelectors.itemIdsMatchingFilter(
      globalFilter,
      itemIds,
      displayItemsById,
      superAndChildrenById,
      narrative,
      relationshipsById,
      calendar,
      GLOBAL_FILTER_ID
    );
    const allIds = viewIds
      .map(viewId => {
        // And apply the local filter
        const filteredIds = filtersById.has(viewId)
          ? filterSelectors.itemIdsMatchingFilter(
            filtersById.get(viewId),
            itemIds,
            displayItemsById,
            superAndChildrenById,
            narrative,
            relationshipsById,
            calendar,
            viewId
          )
          : itemIds;
        return filteredIds;
      })
      .flatten()
      .toSet();
    return allIds;
  }
)((state, viewIds) => viewIds.first());

const getItemIdsForViewForMobile = createCachedSelector(
  // fetch parameters for last calculate function
  getItemIdsMatchingFilterForView,
  // calculate result
  filteredIds => filteredIds.toSet()
)(getViewId);

const getSelectedItemIdsForView = createCachedSelector(
  getItemIdsForView,
  uiStateSelectors.getSelectedItems,
  (idsForView, selectedIds) => selectedIds.filter(si => idsForView.has(si)).toSet()
)(getViewId);

/**
 *
 * @param {List} itemsOfView All items in the view
 * @param {List} filteredItemIds The item ids that should keep after filtering
 * @param {Map} ancestorsById Map of a Set containing all ancestors for each ID
 */
const filterItemsByIds = (itemsOfViewMap, filteredItemIdsSet, ancestorsById) => {
  // We want to keep all items in the filteredItemIdsSet, plus the ancestors of any of those items
  const itemsToKeep = filteredItemIdsSet.withMutations(result => {
    filteredItemIdsSet.forEach(id => {
      const ancestors = ancestorsById.get(id);
      return ancestors ? result.union(ancestors) : result;
    });
  });
  return itemsOfViewMap.filter(item => itemsToKeep.has(item.id)).toList();
};

const getAncestorsMapForViewType = (state, viewIdOrIds) => {
  const firstViewId = viewIdOrIds.first ? viewIdOrIds.first() : viewIdOrIds;
  const viewType = appStateSelectors.getViewTypeForView(state, firstViewId);
  if (viewType === NARRATIVE_VIEW || viewType === OUTLINE_VIEW) {
    return narrativeSelectors.getNarrativeAncestorsFromState(state);
  }
  return superAndChildrenSelectors.getAncestorsMapFromState(state);
};

const getAncestorsMapForViewTypeMobile = state => {
  const viewType = appStateSelectors.getMobilePortraitViewType(state);
  if (viewType === ITEM_CARD_NARRATIVE_LIST_VIEW) {
    return narrativeSelectors.getNarrativeAncestorsFromState(state);
  }
  return superAndChildrenSelectors.getAncestorsMapFromState(state);
};

const getItemsInGroupForView = createCachedSelector(
  // fetch parameters for last calculate function
  getDisplayItemsForViewTypeOfView,
  getItemIdsForView,
  getAncestorsMapForViewType,
  // calculate result
  filterItemsByIds
)(getViewId);

const getSelectedItemsInGroupForView = createCachedSelector(
  // fetch parameters for last calculate function
  getDisplayItemsForViewTypeOfView,
  getSelectedItemIdsForView,
  getAncestorsMapForViewType,
  // calculate result
  filterItemsByIds
)(getViewId);

/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 * @param {number} maxWidth
 */
const getItemsInGroupForViewForMobile = createCachedSelector(
  // fetch parameters for last calculate function
  (state, _viewId, maxWidth, showDragHandles) =>
    getDisplayItemsForViewTypeOfViewForMobile(state, maxWidth, showDragHandles),
  getItemIdsForViewForMobile,
  getAncestorsMapForViewTypeMobile,
  // calculate result
  filterItemsByIds
)(getViewId);

/**
 * Gets ItemRecords for every item in any of the views.
 * Assumes that all views are of the same type
 * @param (TimelineRecord) state
 * @param (List) viewIds
 */
const getItemsInGroupForViews = createCachedSelector(
  // fetch parameters for last calculate function
  (state, viewIds) => getDisplayItemsForViewTypeOfView(state, viewIds.first()),
  getItemIdsForViewIds,
  getAncestorsMapForViewType,
  // calculate result
  (displayItemsMap, itemIdsSet, ancestorsById) => {
    return filterItemsByIds(displayItemsMap, itemIdsSet, ancestorsById);
  }
)((state, viewIds) => viewIds.first());

const getEarliestItemInGroupForView = createCachedSelector(
  // fetch parameters for last calculate function
  state => state,
  // calculate result
  state => {
    const result = itemSelectors.getEarliestEventFromIds(
      state,
      state.getIn(["data", "items", "allIds"]).filter(id => itemSelectors.getItemIsEvent(state, id))
    );
    return result;
  }
)(getViewId);

export default {
  getViewId,
  getItemsInGroupForView,
  getItemsInGroupForViews,
  getSelectedItemsInGroupForView,
  getItemsInGroupForViewForMobile,
  getItemIdsMatchingFilterForView,
  getSelectedItemIdsMatchingFilterForView,
  getEarliestItemInGroupForView,
  getDisplayItemsForViewType,
  getDisplayItemsForViewTypeOfView,
  getTimelineViewDisplayItemForId
};
